/**
 * Sprint O4 · Feb 2026 — Cerrar una Work Order: sólo nota de entrega + firma.
 *
 * Los pagos se manejan estrictamente desde el Invoice de origen. Esta modal
 * NO captura pagos ni muestra saldos pendientes. Llama a
 * POST /api/orders/{id}/deliver (idempotente) con únicamente los campos de
 * entrega. Al confirmar, la orden queda en `delivered` y desaparece del
 * listado de Activas.
 */
import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";
import SignaturePad from "./SignaturePad";

export default function DeliverOrderModal({ order, open, onClose, onDelivered }) {
  const { t } = useI18n();
  const [note, setNote] = useState("");
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const sigRef = useRef(null);

  if (!open || !order) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const body = {
        delivery_note: note,
        signature_data_url: signatureDataUrl,
        mark_items_delivered: true,
      };
      const { data } = await axios.post(`${API}/orders/${order.id}/deliver`, body);
      toast.success("Orden marcada como entregada");
      onDelivered?.(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al entregar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={DASHBOARD.deliverOrderModal}
    >
      <div
        className="bg-white border-2 border-black w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="p-4 border-b-2 border-black flex justify-between items-center">
          <div>
            <div className="label-overline">CERRAR ORDEN</div>
            <h2 className="font-display text-2xl font-black tracking-tighter">
              Entregar #{order.wo_number || order.invoice_number}
            </h2>
          </div>
          <button
            data-testid={DASHBOARD.deliverOrderClose}
            type="button"
            onClick={onClose}
            className="text-2xl leading-none px-2"
          >
            ×
          </button>
        </header>

        <div className="p-4 space-y-5">
          <div className="grid grid-cols-1 gap-3 text-sm">
            <div>
              <div className="label-overline">CLIENTE</div>
              <div className="font-display font-bold">{order.company_name}</div>
            </div>
          </div>

          <div>
            <label className="label-overline block mb-1.5">Nota de entrega</label>
            <textarea
              data-testid={DASHBOARD.deliverOrderNote}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Ej. Entregado a Juan en recepción"
              className="w-full border border-[var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-black"
            />
          </div>

          <div>
            <label className="label-overline block mb-1.5">Firma del cliente (opcional)</label>
            <SignaturePad
              ref={sigRef}
              onChange={setSignatureDataUrl}
            />
          </div>
        </div>

        <footer className="p-4 border-t-2 border-black flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="border border-black px-4 py-2 font-display font-bold text-sm hover:bg-black hover:text-white transition-colors"
          >
            Cancelar
          </button>
          <button
            data-testid={DASHBOARD.deliverOrderConfirm}
            type="button"
            disabled={submitting}
            onClick={handleSubmit}
            className="bg-black text-white px-4 py-2 font-display font-bold text-sm disabled:opacity-50"
          >
            {submitting ? "Procesando…" : "Marcar como entregado"}
          </button>
        </footer>
      </div>
    </div>
  );
}
